import React, { useRef, useState, useCallback, useEffect } from 'react';

// Free Spin wheel component
// Props:
// - apiBaseUrl: string (e.g., https://api2b.pro)
// - token?: string | null
// - logoUrl?: string | null
// - onResult?: (amount: number) => void

export default function FreeSpin({
  apiBaseUrl,
  token = (typeof window !== 'undefined' ? localStorage.getItem('token') : null),
  logoUrl,
  onResult,
}) {
  const wheelRef = useRef(null);

  // Only decorate the page background on the bd170.com origin
  const isBd170 =
    typeof document !== 'undefined' &&
    (document.documentElement.getAttribute('data-origin') || '') === 'bd170';

  const [prizes, setPrizes] = useState([]);
  const [loadingPrizes, setLoadingPrizes] = useState(true);
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [spinDuration, setSpinDuration] = useState(12);
  const [pendingIndex, setPendingIndex] = useState(null);
  const [wonAmount, setWonAmount] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const sliceCount = prizes.length;
  const sliceAngle = sliceCount > 0 ? 360 / sliceCount : 45;

  // Fetch wheel amounts on mount from /api/freespin/values -> { amounts: [] }
  useEffect(() => {
    fetch(`${apiBaseUrl}/api/freespin/values`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data.amounts) && data.amounts.length > 0) {
          setPrizes(data.amounts.map((a) => String(a)));
        }
      })
      .catch((err) => console.error('Failed to fetch freespin values:', err))
      .finally(() => setLoadingPrizes(false));
  }, [apiBaseUrl]);

  const normalize = (angle) => {
    let a = angle % 360;
    if (a < 0) a += 360;
    return a;
  };

  const centerAngleOf = (index) => index * sliceAngle + sliceAngle / 2;

  const spinToIndex = useCallback((targetIndex) => {
    const current = normalize(rotation);
    const center = centerAngleOf(targetIndex);
    const margin = Math.max(2, Math.min(8, sliceAngle / 6));
    const epsRange = (sliceAngle / 2) - margin;
    const epsilon = (Math.random() * 2 * epsRange) - epsRange;
    const desired = normalize(360 - (center + epsilon));
    let baseDelta = desired - current;
    if (baseDelta < 0) baseDelta += 360;
    const extraTurns = 5 + Math.floor(Math.random() * 3); // 5-7 full turns
    const totalDelta = extraTurns * 360 + baseDelta;
    // Random spin duration 10-15 seconds
    const duration = 10 + Math.random() * 5;

    setSpinDuration(duration);
    setSpinning(true);
    setPendingIndex(targetIndex);
    setShowResult(false);
    setErrorMsg(null);
    setRotation((prev) => prev + totalDelta);
  }, [rotation, sliceAngle]);

  const spin = useCallback(async () => {
    if (spinning || prizes.length === 0) return;

    setErrorMsg(null);

    // Call /api/free-spin/run to get the winning amount from server
    let runResult;
    try {
      const res = await fetch(`${apiBaseUrl}/api/freespin/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setErrorMsg(data?.message || 'Something went wrong. Please try again.');
        return;
      }

      runResult = data;
    } catch (err) {
      console.error('Free spin run error:', err);
      setErrorMsg('Network error. Please try again.');
      return;
    }

    // Resolve target slice from server amount
    // API returns {"amdount": 0} (typo in spec) or {"amount": 0}
    const amount = runResult?.amount ?? runResult?.amdount ?? 0;
    let targetIndex = prizes.indexOf(String(amount));
    if (targetIndex === -1) {
      // amount not found in labels - pick random slice
      targetIndex = Math.floor(Math.random() * prizes.length);
    }

    spinToIndex(targetIndex);
  }, [spinning, prizes, spinToIndex, token, apiBaseUrl]);

  const onTransitionEnd = async (ev) => {
    if (ev.propertyName !== 'transform' || !spinning) return;
    setSpinning(false);

    let idx = pendingIndex;
    if (idx == null) {
      const finalAngle = normalize(rotation);
      const pointerAngle = normalize(360 - finalAngle);
      idx = Math.floor(pointerAngle / sliceAngle);
    }

    const amtLabel = prizes[idx] ?? '';
    setWonAmount(amtLabel);

    // POST to /api/freespin - all verification is server-side
    try {
      const res = await fetch(`${apiBaseUrl}/api/freespin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setErrorMsg(data?.message || 'Spin could not be claimed. Please try again.');
        return;
      }

      setShowResult(true);
      const finalAmount = parseInt(String(amtLabel).replace(/[^\d]/g, ''), 10);
      if (onResult) onResult(finalAmount);
    } catch (err) {
      console.error('Free spin claim error:', err);
      setErrorMsg('Network error while claiming spin.');
    }
  };

  if (loadingPrizes) {
    return (
      <div className="fs-overlay">
        <div className="fs-modal" style={{ color: '#fff', fontSize: 20 }}>Loading...</div>
      </div>
    );
  }

  return (
    <div className="fs-overlay">
      {isBd170 && (
        <div className="fs-gifts" aria-hidden="true">
          <span className="fs-gift fs-gift-1">🎁</span>
          <span className="fs-gift fs-gift-2">🎁</span>
          <span className="fs-gift fs-gift-3">🪙</span>
          <span className="fs-gift fs-gift-4">💰</span>
          <span className="fs-gift fs-gift-5">🎁</span>
          <span className="fs-gift fs-gift-6">✨</span>
          <span className="fs-gift fs-gift-7">🪙</span>
          <span className="fs-gift fs-gift-8">🎁</span>
          <span className="fs-gift fs-gift-9">✨</span>
          <span className="fs-gift fs-gift-10">💎</span>
        </div>
      )}
      <div className="fs-modal">
        <div className="fs-wheel-container">
          <div className="fs-pointer" />
          <div
            ref={wheelRef}
            className="fs-wheel"
            style={{
              transform: `rotate(${rotation}deg)`,
              transition: spinning
                ? `transform ${spinDuration}s cubic-bezier(.17,.67,.32,1)`
                : 'none',
            }}
            onTransitionEnd={onTransitionEnd}
          >
            {prizes.map((p, i) => (
              <div
                className="fs-slice-number"
                key={i}
                style={{ '--angle': `${i * sliceAngle + sliceAngle / 2}deg` }}
              >
                ৳{p}
              </div>
            ))}
            <div className="fs-center-circle">
              {logoUrl ? (
                <img src={logoUrl} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              ) : null}
            </div>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <button disabled={spinning} onClick={spin} className="fs-spin-btn">
            {spinning ? 'Spinning...' : 'SPIN'}
          </button>
        </div>

        {showResult && (
          <div className="fs-result-overlay">
            <div className="fs-result-box">
              Congratulations!<br />
              You won <span className="fs-amount">{wonAmount}</span><br />
              <button className="fs-close-btn" onClick={() => setShowResult(false)}>OK</button>
            </div>
          </div>
        )}

        {errorMsg && (
          <div className="fs-result-overlay">
            <div className="fs-result-box">
              {errorMsg}<br />
              <button className="fs-close-btn" onClick={() => setErrorMsg(null)}>OK</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}